import { useEffect, useRef, useState } from 'react';
import type { default as LottieComponent } from 'lottie-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { usePrefersReducedMotion } from '../use-prefers-reduced-motion';
import { useInView } from '../use-in-view';

type LottieData = Record<string, unknown>;

/** Loaded after mount: `lottie-web` touches `window`/`document` at import. */
export function LottiePlayer({
	animationData,
	className,
	loop = true,
	playWhenVisible = false,
	fit = 'contain',
	ariaLabel,
}: {
	animationData: LottieData;
	className?: string;
	loop?: boolean;
	/** For a `loop={false}` one-shot, which would otherwise finish two screens down. Latches: no rewind. */
	playWhenVisible?: boolean;
	/** `cover` crops anchored to the top, so the bottom of the drawing is what gets cut. */
	fit?: 'contain' | 'cover';
	ariaLabel?: string;
}) {
	const reduce = usePrefersReducedMotion();
	const boxRef = useRef<HTMLDivElement>(null);
	const inView = useInView(boxRef);
	const [Lottie, setLottie] = useState<typeof LottieComponent | null>(null);

	useEffect(() => {
		let alive = true;
		import('lottie-react').then((m) => {
			// CJS: Vite's interop can nest the component as `m.default.default`.
			const d = m.default as typeof LottieComponent & { default?: typeof LottieComponent };
			if (alive) setLottie(() => d.default ?? d);
		});
		return () => {
			alive = false;
		};
	}, []);

	// Reserves the box only if the caller's box doesn't depend on the player (an `h-full` in an auto parent does; see `CoinsTile`).
	if (!Lottie || (playWhenVisible && !inView))
		return <div ref={boxRef} className={className} aria-label={ariaLabel} role="img" />;

	// Reduced motion holds the LAST frame: these are build-ons, so frame 0 is an empty box.
	const end = typeof animationData.op === 'number' ? animationData.op : undefined;

	return (
		<Lottie
			animationData={animationData}
			loop={reduce ? false : loop}
			autoplay={!reduce}
			initialSegment={reduce && end ? [Math.max(0, end - 1), end] : undefined}
			className={cn(className)}
			aria-label={ariaLabel}
			role="img"
			rendererSettings={{ preserveAspectRatio: fit === 'cover' ? 'xMidYMin slice' : 'xMidYMid meet' }}
		/>
	);
}
